import { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    LayoutDashboard, Network, CreditCard,
    Layers, UserCog, Activity, Megaphone, LogOut, Shield, Mail, Rocket, Menu, X, UserCheck, Trophy, BarChart3, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const adminNavItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/admin/overview' },
    { icon: Network, label: 'Rete & Manager', path: '/admin/network' },
    { icon: CreditCard, label: 'Prezzi', path: '/admin/pricing' },
    { icon: Layers, label: 'Servizi', path: '/admin/services' },
    { icon: UserCheck,      label: 'Moduli Censimento', path: '/admin/censimento-moduli' },
    { icon: Trophy,          label: 'Championship',      path: '/admin/championship' },
    { icon: BarChart3,       label: 'MRR Monitoring',    path: '/admin/mrr' },
    { icon: AlertTriangle,   label: 'Contestazioni',     path: '/admin/disputes' },
    { icon: UserCog,         label: 'Impersona',         path: '/admin/impersonate' },
    { icon: Activity, label: 'Attività', path: '/admin/activity' },
    { icon: Megaphone, label: 'Broadcast', path: '/admin/broadcast' },
    { icon: Mail, label: 'Email Templates', path: '/admin/email-templates' },
    { icon: Rocket, label: 'Roadmap', path: '/admin/changelog' },
];

const AdminLayout = () => {
    const { profile, signOut } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const sidebarContent = (
        <>
            {/* Logo */}
            <div className="flex flex-col items-center py-6 px-4 border-b border-white/10">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl mb-3"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                    <Shield className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-bold text-sm tracking-wide">ONET Admin</p>
                <span className="mt-1.5 text-[10px] font-semibold tracking-widest uppercase px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(124,58,237,0.2)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.35)' }}>
                    Superuser
                </span>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                {adminNavItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => cn(
                            'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all group relative',
                            isActive
                                ? 'bg-violet-600/20 text-violet-300 font-medium'
                                : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && (
                                    <motion.div
                                        layoutId="adminActiveIndicator"
                                        className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r-full"
                                        style={{ background: 'linear-gradient(to bottom, #7c3aed, #4f46e5)' }}
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                                    />
                                )}
                                <item.icon className={cn('w-4 h-4 flex-shrink-0', isActive && 'text-violet-400')} />
                                <span>{item.label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Bottom: user + logout */}
            <div className="p-3 border-t border-white/10 space-y-2">
                {profile && (
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                            {(profile.name?.[0] || profile.email?.[0])?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-200 truncate">{profile.name || profile.email}</p>
                            <p className="text-[10px] text-violet-400">Amministratore</p>
                        </div>
                    </div>
                )}
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 border border-red-500/20 transition-all"
                >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                </button>
            </div>
        </>
    );

    return (
        <div className="flex h-screen bg-[#0a0a0f] overflow-hidden">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar desktop */}
            <motion.aside
                initial={{ x: -24, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="hidden lg:flex w-64 flex-shrink-0 flex-col m-3 rounded-2xl border border-white/10 overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #13111f 0%, #0d0b18 100%)' }}
            >
                {sidebarContent}
            </motion.aside>

            {/* Sidebar mobile drawer */}
            <div className={`
                fixed inset-y-0 left-0 z-50 w-64 flex flex-col
                border-r border-white/10 overflow-hidden
                transition-transform duration-300 ease-in-out lg:hidden
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `} style={{ background: 'linear-gradient(160deg, #13111f 0%, #0d0b18 100%)' }}>
                <button
                    onClick={() => setSidebarOpen(false)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
                {sidebarContent}
            </div>

            {/* Main */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Mobile top bar */}
                <div className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0a0a0f]">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-violet-400" />
                        <span className="text-white font-semibold text-sm">Admin Panel</span>
                    </div>
                </div>

                <main className="flex-1 overflow-y-auto lg:pt-0 pt-14">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
